import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ConversionGoal } from "@/types/analytics.types";

interface UseGoalProgressOptions {
  startDate: string;
  endDate: string;
}

export const useGoalProgress = ({ startDate, endDate }: UseGoalProgressOptions) => {
  return useQuery({
    queryKey: ['goal-progress', startDate, endDate],
    queryFn: async (): Promise<ConversionGoal[]> => {
      // Fetch all active goals
      const { data: goals, error: goalsError } = await supabase
        .from('conversion_goals')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (goalsError) throw goalsError;

      if (!goals) return [];

      // Calculate current value for each goal
      const goalsWithProgress = await Promise.all(
        goals.map(async (goal) => {
          let currentValue = 0;

          if (goal.event_name === 'lead_captured') {
            // Count leads
            const { count, error } = await supabase
              .from('checklist_leads')
              .select('*', { count: 'exact', head: true })
              .gte('created_at', startDate)
              .lte('created_at', endDate);

            if (error) throw error;
            currentValue = count || 0;
          } else if (goal.event_name === 'consultation_requested') {
            // Count consultation requests
            const { count, error } = await supabase
              .from('checklist_leads')
              .select('*', { count: 'exact', head: true })
              .eq('wants_consultation', true)
              .gte('created_at', startDate)
              .lte('created_at', endDate);

            if (error) throw error;
            currentValue = count || 0;
          } else {
            // Count events
            const { count, error } = await supabase
              .from('analytics_events')
              .select('*', { count: 'exact', head: true })
              .eq('event_name', goal.event_name)
              .gte('created_at', startDate)
              .lte('created_at', endDate);

            if (error) throw error;
            currentValue = count || 0;
          }

          // Calculate progress percentage
          const progress = goal.target_value 
            ? Math.round((currentValue / goal.target_value) * 100)
            : 0;

          // Calculate change from previous period (simplified - would need historical data)
          const change = 0; // TODO: Implement comparison with previous period

          return {
            ...goal,
            current_value: currentValue,
            progress,
            change,
          };
        })
      );

      return goalsWithProgress;
    },
    refetchInterval: 60000, // Refetch every minute
  });
};
